// Season editor — the selected sensor's details, editable (or the "Add new"
// form). The fields per sensor type, and what each one writes, are
// production's Edit Field sensor editor (dashboard/field/EditSensor.jsx); the
// rules live in sensorEditRules.js. ValvesInput is production's component.
const React = require('react');
const { useTranslation } = require('react-i18next');
const dayjs = require('../../utils/dayjsConfig');
const ValvesInput = require('ValvesInput');
const {
  TENSIOMETER_RANGE_OPTIONS,
} = require('../dashboard/field/tensiometerRangeHelpers');
const rules = require('./sensorEditRules');

const { MIDDLE_FIELDS } = rules;

const DATE_INPUT_FORMAT = 'YYYY-MM-DD';

// The icons of Sensor.jsx's row menu.
const LOCATE_ICON = require('images/icons/edit_field/navigate_icon.svg');
const DELETE_ICON = require('images/icons/trash.svg');

const Row = ({ label, children }) => (
  <div className='ns-sensor-detail-element'>
    <label className='ns-sensor-detail-label'>{label}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, type = 'text', disabled = false }) => (
  <input
    className='ns-sensor-detail-input'
    type={type}
    value={value ?? ''}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
  />
);

const SelectInput = ({ value, options, onChange, disabled, placeholder }) => (
  <select
    className='ns-sensor-detail-input custom-select'
    value={value ?? ''}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
  >
    {placeholder !== undefined && <option value=''>{placeholder}</option>}
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.title}
      </option>
    ))}
  </select>
);

const toSelectOptions = (menuOptions) =>
  menuOptions.map((option) => ({
    title: option.title,
    value: option.value.type,
  }));

const TYPE_SELECT_OPTIONS = toSelectOptions(rules.SENSOR_TYPE_OPTIONS);
const FARMING_UNIT_TYPE_SELECT_OPTIONS = toSelectOptions(
  rules.FARMING_UNIT_SENSOR_TYPE_OPTIONS,
);

const TypeRow = ({ draft, isNew, isFarmingUnit, onTypeChange }) => {
  const { t } = useTranslation();
  const type = draft.chosenType.type;
  const menu = isFarmingUnit
    ? FARMING_UNIT_TYPE_SELECT_OPTIONS
    : TYPE_SELECT_OPTIONS;
  // An existing sensor's type is fixed (Edit Field: existingFieldDisableType);
  // a type missing from the menu ("ndrip wm", a pulse in the farming unit
  // modal) is shown as is.
  const options = menu.some((option) => option.value === type)
    ? menu
    : [{ title: type, value: type }].concat(menu);
  return (
    <Row label={t('type')}>
      <SelectInput
        value={type}
        options={options}
        disabled={!isNew}
        onChange={onTypeChange}
      />
    </Row>
  );
};

const TransmitterRow = ({ draft, farmingUnitSensors, onDraftChange }) => {
  const { t } = useTranslation();
  if (draft.chosenType.type === 'tal-gil' && farmingUnitSensors) {
    const options = Object.keys(farmingUnitSensors).map((serial) => ({
      title: farmingUnitSensors[serial].transmitter_id,
      value: serial,
    }));
    return (
      <Row label={t('transmitter_id')}>
        <SelectInput
          value={draft.talGilTransmitterId}
          options={options}
          placeholder={t('please select')}
          onChange={(serial) =>
            serial &&
            onDraftChange(
              rules.linkDraftToFarmingUnitSensor(
                draft,
                farmingUnitSensors[serial],
                serial,
              ),
            )
          }
        />
      </Row>
    );
  }
  return (
    <Row label={t('transmitter_id')}>
      <TextInput
        value={draft.transmitterId}
        onChange={(value) =>
          onDraftChange({
            ...draft,
            transmitterId: rules.normalizeTransmitterId(value),
          })
        }
      />
    </Row>
  );
};

const MiddleRow = ({ draft, field, lengthUnit, onDraftChange, valvesKey }) => {
  const { t } = useTranslation();
  const set = (key) => (value) => onDraftChange({ ...draft, [key]: value });
  if (field === MIDDLE_FIELDS.VALVES) {
    return (
      <Row label={t('valves')}>
        <ValvesInput
          key={valvesKey}
          valves={draft.valves}
          onAdd={set('valves')}
        />
      </Row>
    );
  }
  if (field === MIDDLE_FIELDS.M3_PER_PULSE) {
    return (
      <Row label={t('water_meter_units')}>
        <TextInput value={draft.m3PerPulse} onChange={set('m3PerPulse')} />
      </Row>
    );
  }
  if (field === MIDDLE_FIELDS.OFFSET) {
    return (
      <Row label={`${t('offset')} (${lengthUnit})`}>
        <TextInput
          type='number'
          value={draft.offset}
          onChange={set('offset')}
        />
      </Row>
    );
  }
  if (field === MIDDLE_FIELDS.INSTALLATION_DEPTH) {
    return (
      <Row label={`${t('installation_depth')} (${lengthUnit})`}>
        <TextInput
          type='number'
          value={draft.installationDepth}
          onChange={set('installationDepth')}
        />
      </Row>
    );
  }
  if (field === MIDDLE_FIELDS.TENSIOMETER_RANGE) {
    return (
      <Row label={t('tensiometer_range')}>
        <SelectInput
          value={draft.wpSubType}
          options={TENSIOMETER_RANGE_OPTIONS}
          onChange={set('wpSubType')}
        />
      </Row>
    );
  }
  return null;
};

const PressureRows = ({ draft, lengthUnit, onDraftChange }) => {
  const { t } = useTranslation();
  const unit = lengthUnit === 'Inch' ? 'PSI' : 'bar';
  return (
    <>
      <Row label={`${t('max')} (${unit})`}>
        <TextInput
          type='number'
          value={draft.pressureMax}
          onChange={(value) => onDraftChange({ ...draft, pressureMax: value })}
        />
        {rules.isDraftInvalid(draft) && (
          <div className='ns-error-text'>{t('max_bigger_than_min')}</div>
        )}
      </Row>
      <Row label={`${t('min')} (${unit})`}>
        <TextInput
          type='number'
          value={draft.pressureMin}
          onChange={(value) => onDraftChange({ ...draft, pressureMin: value })}
        />
      </Row>
    </>
  );
};

const LocationRows = ({ draft, readOnly, onDraftChange }) => {
  const { t } = useTranslation();
  return (
    <>
      <Row label={t('latitude')}>
        <TextInput
          type='number'
          value={draft.latitude}
          disabled={readOnly}
          onChange={(value) => onDraftChange({ ...draft, latitude: value })}
        />
      </Row>
      <Row label={t('longitude')}>
        <TextInput
          type='number'
          value={draft.longitude}
          disabled={readOnly}
          onChange={(value) => onDraftChange({ ...draft, longitude: value })}
        />
      </Row>
    </>
  );
};

const fertilizerOptionsOf = (fertilizerData, t) =>
  Object.keys(fertilizerData || {}).map((fertType) => ({
    title: t(fertilizerData[fertType].fertilizer_type),
    value: fertType,
  }));

const toDateInput = (unixDate) =>
  unixDate ? dayjs.unix(unixDate).format(DATE_INPUT_FORMAT) : '';

const FertilizerRow = ({
  meterId,
  index,
  row,
  readOnly,
  options,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className='ns-sensor-fert-row'>
      <Row label={t('Fert_sensor_fertlization_type')}>
        <SelectInput
          value={row.type}
          options={options}
          disabled={readOnly}
          onChange={(type) =>
            onChange((fert) => rules.setFertRowType(fert, meterId, index, type))
          }
        />
      </Row>
      <Row label={t('fertilizer_meter_id')}>
        <TextInput
          value={meterId}
          disabled={readOnly}
          onChange={(value) =>
            onChange((fert) =>
              rules.setFertRowMeterId(fert, meterId, index, value),
            )
          }
        />
      </Row>
      <Row label={t('start_date')}>
        <input
          type='date'
          className='ns-sensor-detail-input'
          value={toDateInput(row.start_date)}
          disabled={readOnly}
          onChange={(event) =>
            onChange((fert) =>
              rules.setFertRowStartDate(
                fert,
                meterId,
                index,
                dayjs(event.target.value).unix(),
              ),
            )
          }
        />
      </Row>
      {!readOnly && (
        <button
          type='button'
          className='ns-link-btn ns-danger-text'
          onClick={() =>
            onChange((fert) => rules.removeFertRow(fert, meterId, index))
          }
        >
          {t('delete')}
        </button>
      )}
    </div>
  );
};

const FertilizerRows = ({ draft, fertilizerData, readOnly, onDraftChange }) => {
  const { t } = useTranslation();
  const options = fertilizerOptionsOf(fertilizerData, t);
  const onChange = (change) =>
    onDraftChange({ ...draft, fertMeters: change(draft.fertMeters) });
  return Object.keys(draft.fertMeters || {}).map((meterId) =>
    draft.fertMeters[meterId].map((row, index) => (
      <FertilizerRow
        key={meterId + ':' + index}
        meterId={meterId}
        index={index}
        row={row}
        readOnly={readOnly}
        options={options}
        onChange={onChange}
      />
    )),
  );
};

// EditSensor's "Add fertilizer" (farming unit modal only).
const AddFertilizerButton = ({ draft, onDraftChange }) => {
  const { t } = useTranslation();
  return (
    <button
      type='button'
      className='ns-link-btn'
      onClick={() =>
        onDraftChange({
          ...draft,
          fertMeters: rules.addFertRow(draft.fertMeters),
        })
      }
    >
      + {t('add_fertilizer')}
    </button>
  );
};

const LinkedNote = () => {
  const { t } = useTranslation();
  return (
    <div className='ns-sensor-note'>{t('Farming_unit_entity_tooltip')}</div>
  );
};

// The fields of one sensor (or of the "Add new" form). `isFarmingUnit` is
// EditSensor's `farmingUnitModal`: the farming unit's own sensors.
const SensorFields = ({
  draft,
  isNew,
  isFarmingUnit = false,
  onDraftChange,
  onTypeChange,
  farmingUnitSensors,
  fertilizerData,
  lengthUnit,
  showTensiometerRange,
  valvesKey,
}) => {
  const { t } = useTranslation();
  const readOnly = rules.isLinkedToFarmingUnit(draft);
  const middleField = rules.middleFieldOf(
    draft.chosenType,
    showTensiometerRange,
    isFarmingUnit,
  );
  return (
    <>
      <TypeRow
        draft={draft}
        isNew={isNew}
        isFarmingUnit={isFarmingUnit}
        onTypeChange={onTypeChange}
      />
      {!rules.isAIIrrigator(draft) && (
        <>
          <TransmitterRow
            draft={draft}
            farmingUnitSensors={farmingUnitSensors}
            onDraftChange={onDraftChange}
          />
          <Row label={t('name')}>
            <TextInput
              value={draft.name}
              disabled={readOnly}
              onChange={(value) => onDraftChange({ ...draft, name: value })}
            />
          </Row>
          <MiddleRow
            draft={draft}
            field={middleField}
            lengthUnit={lengthUnit}
            onDraftChange={onDraftChange}
            valvesKey={valvesKey}
          />
          {rules.hasPressureBoundaries(draft) && (
            <PressureRows
              draft={draft}
              lengthUnit={lengthUnit}
              onDraftChange={onDraftChange}
            />
          )}
          <LocationRows
            draft={draft}
            readOnly={readOnly}
            onDraftChange={onDraftChange}
          />
        </>
      )}
      <FertilizerRows
        draft={draft}
        fertilizerData={fertilizerData}
        readOnly={readOnly}
        onDraftChange={onDraftChange}
      />
      {rules.canAddFertilizer(draft, isFarmingUnit) && (
        <AddFertilizerButton draft={draft} onDraftChange={onDraftChange} />
      )}
      {readOnly && <LinkedNote />}
    </>
  );
};

const Checkbox = ({ label, checked, onChange }) => (
  <label className='ns-sensor-check'>
    <input type='checkbox' checked={Boolean(checked)} onChange={onChange} />
    <span>{label}</span>
  </label>
);

// Sensor.jsx's row menu: settings, locate and delete.
const SensorSettings = ({ sensor, actions }) => {
  const { t } = useTranslation();
  return (
    <div className='ns-sensor-settings'>
      <Checkbox
        label={
          sensor.type === 'wp'
            ? t('ignore_sensor_during_calculations')
            : t('ignore_sensor_during_calculations_and_hide')
        }
        checked={sensor.ignore_in_calculations}
        onChange={actions.onToggleIgnore}
      />
      <Checkbox
        label={t('stop_data_collection')}
        checked={sensor.paused}
        onChange={actions.onTogglePaused}
      />
      {rules.canConsiderAsFlowMeter(sensor.type) && (
        <Checkbox
          label={t('consider_as_flow_meter')}
          checked={sensor.should_consider_as_wfr}
          onChange={actions.onToggleWFR}
        />
      )}
      <div className='ns-sensor-actions'>
        <button
          type='button'
          className='ns-btn ns-btn-outline ns-btn-icon'
          onClick={actions.onLocate}
        >
          <img src={LOCATE_ICON} alt='' />
          {t('locate_coordinates')}
        </button>
        <button
          type='button'
          className='ns-btn ns-btn-outline ns-btn-icon ns-danger-text'
          onClick={actions.onDelete}
        >
          <img src={DELETE_ICON} alt='' />
          {t('delete')}
        </button>
      </div>
    </div>
  );
};

const PLUS_ICON = require('images/icons/plus_icon.svg');

// The sensors card shared by the season editor's sensors section and the
// farming unit editor's Sensors tab: "Sensors list" + "+" header, the table
// (children) and, when a sensor or Add new is open, its details beside it.
const SensorsCard = ({
  isOpen,
  addDisabled,
  onAdd,
  details,
  className = '',
  children,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={
        'ns-sensors-card' +
        (isOpen ? ' ns-sensors-card-open' : '') +
        (className ? ' ' + className : '')
      }
    >
      <div className='ns-sensors-header'>
        <div className='text-style-titles-standard-14-semi-bold'>
          {t('new_season_sensors_table')}
        </div>
        <button
          type='button'
          className='ns-icon-btn'
          disabled={addDisabled}
          title={t('add_new_button')}
          onClick={onAdd}
        >
          <img src={PLUS_ICON} className='img' alt={t('add_new_button')} />
        </button>
      </div>
      <div className='ns-sensors-body'>
        <div className='ns-sensors-left'>{children}</div>
        {details}
      </div>
    </div>
  );
};

const NewSensorFooter = ({ seasonChoice, canAdd, onAdd, onCancel }) => {
  const { t } = useTranslation();
  return (
    <>
      {seasonChoice && (
        <Row label={t('new_season_block')}>
          <SelectInput
            value={seasonChoice.value}
            options={seasonChoice.options}
            onChange={seasonChoice.onChange}
          />
        </Row>
      )}
      <div className='ns-sensor-actions'>
        <button
          type='button'
          className='ns-btn ns-btn-outline'
          onClick={onCancel}
        >
          {t('cancel')}
        </button>
        <button
          type='button'
          className='ns-btn ns-btn-primary'
          disabled={!canAdd}
          onClick={onAdd}
        >
          {t('done_button')}
        </button>
      </div>
    </>
  );
};

module.exports = {
  SensorFields,
  SensorSettings,
  NewSensorFooter,
  SensorsCard,
};
